/**
 * Location Tracking Service
 * Handles periodic GPS updates to the backend for location sharing features
 */

import { profileApi } from '@api/endpoints/profile'

let locationInterval: ReturnType<typeof setInterval> | null = null
let isTracking = false

const UPDATE_INTERVAL_MS = 30000 // 30 seconds

/**
 * Start periodic location tracking
 * Updates user's current location every 30 seconds
 */
export async function startLocationTracking(): Promise<boolean> {
  if (!navigator.geolocation) {
    console.warn('[LocationTracking] Geolocation not supported by browser')
    return false
  }

  if (isTracking) {
    console.log('[LocationTracking] Already tracking')
    return true
  }

  // Note: We always send location updates regardless of sharing settings.
  // The backend needs location data to check for silent adventures.
  // Sharing settings only control what's visible to other users.

  console.log('[LocationTracking] Starting location tracking')
  isTracking = true

  // Initial update
  updateLocation()

  // Update every 30 seconds
  locationInterval = setInterval(updateLocation, UPDATE_INTERVAL_MS)

  return true
}

/**
 * Stop location tracking
 */
export function stopLocationTracking(): void {
  if (locationInterval) {
    clearInterval(locationInterval)
    locationInterval = null
  }
  isTracking = false
  console.log('[LocationTracking] Stopped location tracking')
}

/**
 * Check if location tracking is currently active
 */
export function isLocationTrackingActive(): boolean {
  return isTracking
}

/**
 * Perform a single location update
 */
function updateLocation(): void {
  navigator.geolocation.getCurrentPosition(
    async (position) => {
      try {
        await profileApi.updateCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString(),
        })
        console.log('[LocationTracking] Location updated:', {
          lat: position.coords.latitude.toFixed(4),
          lng: position.coords.longitude.toFixed(4),
          accuracy: Math.round(position.coords.accuracy),
        })
      } catch (error) {
        console.error('[LocationTracking] Failed to update location:', error)
      }
    },
    (error) => {
      console.error('[LocationTracking] Geolocation error:', error.message)

      // Stop tracking if permission denied
      if (error.code === error.PERMISSION_DENIED) {
        console.warn('[LocationTracking] Permission denied, stopping tracking')
        stopLocationTracking()
      }
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    }
  )
}

/**
 * Request a single immediate location update
 * Useful for on-demand updates outside the regular interval
 */
export function requestImmediateUpdate(): void {
  if (!navigator.geolocation) {
    console.warn('[LocationTracking] Geolocation not supported')
    return
  }
  updateLocation()
}
